import { nanoid } from 'nanoid'

const UUID_BYTE_LENGTH = 16

/**
 * RFC 4122 v4 UUID from `crypto.getRandomValues` — the fallback for runtimes
 * where `crypto.randomUUID` is missing: browsers on a plain-http origin (a
 * LAN preview, a non-localhost dev box) expose `getRandomValues` but gate
 * `randomUUID` behind a secure context.
 */
function uuidFromRandomValues(): string {
  const bytes = globalThis.crypto.getRandomValues(
    new Uint8Array(UUID_BYTE_LENGTH)
  )
  // Version nibble (0100) and variant bits (10xx), written with arithmetic
  // so the file stays free of bitwise operators.
  bytes[6] = 0x40 + (bytes[6] % 16)
  bytes[8] = 0x80 + (bytes[8] % 64)
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * A fresh v4 UUID. Prefers `crypto.randomUUID()` (Node ≥ 19, every modern
 * browser in a secure context, the Convex isolate — `convex/productEvents/
 * shared.ts` and `convex/insights/email_notifications.ts` already call it)
 * and falls back to `getRandomValues` where `randomUUID` is undefined.
 *
 * Canonical for the `crypto.randomUUID?.() ?? \`x-${Date.now()}-${Math.random()
 * .toString(36)…}\`` fallbacks the 2026-09 condensation audit counted (unit
 * U3.6): `app/(app)/(workspace)/emails/(canvas)/engine/use-engine-presence.ts`,
 * `.../groups-grid/use-group-presence.ts`, `components/global-chat/
 * element-edit-tracker.ts`, `lib/brand/assets/import-queue.ts:597`,
 * `lib/uploads/upload-user-image.ts:189`, and the `cheapId()` in
 * `lib/brand/assets/import_images_launcher.ts`.
 */
export function newId(): string {
  const webCrypto = globalThis.crypto
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }
  return uuidFromRandomValues()
}

/**
 * `${prefix}_${nanoid(size)}` — a prefixed, URL-safe, 126-bit-by-default
 * random id (`grp_V1StGXR8_Z5jdHi6B-myT`).
 *
 * nanoid IS isolate-safe for `convex/`: the Convex CLI bundles isolate
 * functions with esbuild `platform: "browser"`, which selects nanoid's
 * `index.browser.js` (global `crypto.getRandomValues`, no `node:crypto`),
 * and seven convex modules already mint ids with it in production paths
 * (`convex/sends/mutations.ts`, `convex/triggerInstances/_id.ts`,
 * `convex/emails/v1Mutations.ts`, …). `"use node"` actions get the Node
 * build (`node:crypto` webcrypto). So this module is Convex-reachable; its
 * only dependency is nanoid.
 *
 * Canonical for the `Math.random().toString(36)` id mints the 2026-09
 * condensation audit counted (unit U3.6, banned by
 * `scripts/codemods/rules/math-random-id.yml`): `convex/emailTable/
 * grouping_helpers.ts:createGroupId` and its byte-identical twin in
 * `convex/emailTable/mutations.ts` (`grp_`), `convex/importJobs/mutations.ts`
 * (`imp_`), `convex/testFixtures/mutations.ts` (`e2e_email_`,
 * `e2e_emailver_`), the session ids in the two presence hooks (`s_`, `grid_`),
 * `components/global-chat/element-edit-tracker.ts` (`pe_`), the Redis ZSET
 * members in `lib/api/rate_limit/{public-ip-limit,store}.ts`, and the blob
 * filename disambiguators in `lib/core/media/{gif-video-service,mp4-to-gif}.ts`.
 * Sweep caveat: the `grp_`/`imp_` copies embed `Date.now()` — confirm nothing
 * sorts by id before dropping the timestamp. The deterministic FNV/djb2
 * hash → base36 sites are NOT id mints and stay.
 */
export function createPrefixedId(prefix: string, size = 21): string {
  if (prefix.length === 0) {
    throw new RangeError('createPrefixedId: prefix must not be empty')
  }
  if (!Number.isInteger(size) || size <= 0) {
    throw new RangeError(
      `createPrefixedId: size must be a positive integer, got ${size}`
    )
  }
  return `${prefix}_${nanoid(size)}`
}
